<script lang="ts">
  import PageMeta from '$lib/components/PageMeta.svelte';
  import GridLines from '$lib/components/GridLines.svelte';
  import GridContent from '$lib/components/GridContent.svelte';
  import GridInner from '$lib/components/GridInner.svelte';
  import CornerFrame from '$lib/components/CornerFrame.svelte';
  import Star from '$lib/components/Star.svelte';
  import ContactCtaSection from '$lib/sections/ContactCtaSection.svelte';
  import { hatchStyle45 } from '$lib/styles/hatch';
  import { Home, MessageSquare, Phone, Code, Users } from '@lucide/svelte';

  // Hub - featured surface
  const hub = {
    title: 'The Hub',
    headline: 'Your AI command center.',
    description: "A full workspace for every user. A state-of-the-art assistant with a file system, code execution, access to the tools and data sources your organization has connected, and guardrails that govern every interaction.",
    features: [
      'Personal assistant per user with persistent file system and code execution',
      'Organizational agents published by admins and available to teams',
      'Canvas & document workspace with tool outputs and artifacts',
      'Conversation history, context management, and session continuity',
      'Sub-agents for specialized tasks, orchestrated by a personal assistant'
    ],
    icon: Home
  };

  // Other surfaces
  const surfaces = [
    {
      title: 'Embeddable Widget',
      headline: 'AI on any page.',
      description: 'Drop a chat widget into your website, portal, or internal app. One line of code, infinite use cases.',
      features: [
        'Embed anywhere (script tag or SDK)',
        'Customizable styling',
        'Context-aware (knows where it\'s embedded)',
        'Handoff to human agents'
      ],
      icon: MessageSquare
    },
    {
      title: 'Teams & Slack',
      headline: 'AI in the flow of work.',
      description: 'Your people live in Teams and Slack. So should your AI. Native integrations that feel like teammates, not tools.',
      features: [
        '@mention to invoke',
        'Thread-aware conversations',
        'Action buttons and cards',
        'Channel or DM deployment'
      ],
      icon: Users
    },
    {
      title: 'API / Headless',
      headline: 'AI as infrastructure.',
      description: 'Not everything needs a UI. Trigger agents from workflows, embed intelligence in pipelines, build custom frontends on our backend.',
      features: [
        'REST & streaming APIs',
        'Webhook integrations',
        'Custom UI support',
        'Workflow automation'
      ],
      icon: Code
    },
    {
      title: 'Voice / IVR',
      headline: 'AI that picks up the phone.',
      description: 'Call center automation that actually works. Natural conversation, real-time lookup, seamless handoff to humans when needed.',
      features: [
        'Inbound call handling',
        'Real-time knowledge retrieval',
        'Sentiment detection',
        'Human escalation'
      ],
      icon: Phone,
      comingSoon: true
    }
  ];
</script>

<PageMeta
  title="Hub & User Surfaces — AI that meets people where they work"
  description="Production-ready AI interfaces your team can use immediately. The Hub, embeddable widgets, Teams and Slack, API access — one foundation, many entry points."
/>

<!-- Top stars -->
<div class="relative">
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] -top-px" />
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] -top-px" />
</div>

<!-- Hero -->
<section class="bg-[#F9F9F4] py-[80px] lg:py-[120px] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <GridContent>
    <GridInner>
      <div class="max-w-[700px]">
        <h1 class="font-sans text-[48px] lg:text-[64px] tracking-[-0.04em] leading-[1.1] text-[#020202] mb-6">
          AI that meets people where they work.
        </h1>
        <p class="font-mono font-light text-[16px] text-[#5C5855] leading-relaxed">
          Corral ships with production-ready AI interfaces your team can use immediately. But the Hub is one surface. Corral also powers embedded widgets on your site, integrations with Teams and Slack, and a full API for custom surfaces your developers build. One foundation, many entry points — all governed by the same security model, all running in your cloud.
        </p>
      </div>
    </GridInner>
  </GridContent>
</section>

<!-- Divider -->
<div class="h-[48px] bg-[#f9f9f4] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <div class="hidden lg:block absolute bottom-0 left-0 right-0 h-px bg-[#D3D3D3]" aria-hidden="true"></div>
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] bottom-[-9px]" />
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] bottom-[-9px]" />
</div>

<!-- §1: The Hub — Featured full-width card -->
<section class="bg-[#0d0d0d] py-[80px] relative">
  <GridLines variant="dark" />
  <GridContent>
    <GridInner>
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
        <div>
          <div class="flex items-center gap-4 mb-6">
            <CornerFrame
              class="w-[48px] h-[48px] flex items-center justify-center"
              bg="bg-transparent"
              borderColor="rgba(255, 255, 255, 0.3)"
              cornerColor="white"
              cornerSize={8}
            >
              <Home class="w-5 h-5 text-white" />
            </CornerFrame>
            <h2 class="font-sans text-[24px] text-white">{hub.title}</h2>
          </div>
          <h3 class="font-sans text-[36px] lg:text-[48px] text-white tracking-[-0.04em] leading-[1.1] mb-6">
            {hub.headline}
          </h3>
          <p class="font-body font-light text-[16px] text-[#cfcfcf] leading-relaxed">
            {hub.description}
          </p>
        </div>

        <div class="bg-[#171719] border border-[#333335] p-8">
          <h4 class="font-mono text-[12px] text-[#878787] uppercase tracking-wider mb-6">Key capabilities</h4>
          <ul class="flex flex-col gap-4">
            {#each hub.features as feature}
              <li class="flex items-start gap-3">
                <span class="w-1.5 h-1.5 bg-[#ee6018] rounded-full mt-2 shrink-0"></span>
                <span class="font-mono font-light text-[14px] text-[#cfcfcf]">{feature}</span>
              </li>
            {/each}
          </ul>
        </div>
      </div>
    </GridInner>
  </GridContent>
</section>

<!-- Divider -->
<div class="h-[48px] bg-[#0d0d0d] relative">
  <GridLines variant="dark" />
  <div class="hidden lg:block absolute bottom-0 left-0 right-0 h-px bg-[#333335]" aria-hidden="true"></div>
  <Star variant="full" class="hidden lg:block absolute left-[calc(9.03%-9px)] bottom-[-9px]" />
  <Star variant="full" class="hidden lg:block absolute right-[calc(9.03%-9px)] bottom-[-9px]" />
</div>

<!-- §2: Other surfaces — 2x2 grid -->
<section class="bg-[#0d0d0d] py-[80px] relative">
  <GridLines variant="dark" />
  <GridContent>
    <GridInner>
      <div class="max-w-[700px] mx-auto mb-12">
        <h2 class="font-sans text-[36px] lg:text-[48px] text-white tracking-[-0.04em] leading-[1.1]">
          Every entry point. One security model.
        </h2>
      </div>

      <div class="relative">
        <div class="hidden lg:block absolute top-0 -left-[calc(54px+9.03vw)] -right-[calc(54px+9.03vw)] h-px bg-[#333335] z-[5]" aria-hidden="true"></div>
        <Star variant="full" class="hidden lg:block absolute -left-[63px] top-[-9px]" />
        <Star variant="full" class="hidden lg:block absolute -right-[63px] top-[-9px]" />

        <div class="hidden lg:block absolute bottom-0 -left-[calc(54px+9.03vw)] -right-[calc(54px+9.03vw)] h-px bg-[#333335] z-[5]" aria-hidden="true"></div>
        <Star variant="full" class="hidden lg:block absolute -left-[63px] bottom-[-9px]" />
        <Star variant="full" class="hidden lg:block absolute -right-[63px] bottom-[-9px]" />

        <div class="grid grid-cols-1 md:grid-cols-2 gap-0">
          {#each surfaces as surface, i}
            <div class="bg-[#171719] border border-[#333335] p-6 lg:p-8 {i % 2 !== 0 ? 'lg:-ml-px' : ''} {i >= 2 ? '-mt-px' : ''}">
              <div class="flex items-center gap-3 mb-4">
                <CornerFrame
                  class="w-[36px] h-[36px] flex items-center justify-center"
                  bg="bg-transparent"
                  borderColor="rgba(255, 255, 255, 0.2)"
                  cornerColor="white"
                  cornerSize={6}
                >
                  <svelte:component this={surface.icon} class="w-4 h-4 text-white" />
                </CornerFrame>
                <h3 class="font-sans text-[16px] text-white">{surface.title}</h3>
                {#if 'comingSoon' in surface && surface.comingSoon}
                  <span class="font-mono text-[11px] text-[#ee6018] uppercase tracking-wider">2026</span>
                {/if}
              </div>
              <p class="font-mono font-light text-[14px] text-[#878787] leading-relaxed mb-4">
                {surface.description}
              </p>
              <ul class="flex flex-col gap-2">
                {#each surface.features as feature}
                  <li class="font-mono font-light text-[12px] text-[#cfcfcf] flex items-center gap-2">
                    <span class="w-1 h-1 bg-[#ee6018] rounded-full shrink-0"></span>
                    {feature}
                  </li>
                {/each}
              </ul>
            </div>
          {/each}
        </div>
      </div>
    </GridInner>
  </GridContent>
</section>

<!-- Divider: → CTA -->
<div class="h-[48px] bg-[#0d0d0d] relative">
  <GridLines variant="dark" />
  <div class="hidden lg:block absolute bottom-0 left-0 right-0 h-px bg-[#D3D3D3]" aria-hidden="true"></div>
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] bottom-[-9px]" />
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] bottom-[-9px]" />
</div>

<!-- CTA -->
<ContactCtaSection />
